import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { PostHogProvider } from "posthog-js/react";
import App from "./App.tsx";
import "./index.css";
import posthog, { initPostHog } from "./lib/posthog";
import { initCapacitor } from "./lib/capacitor";
import { initPixels } from "./lib/conversion-pixels";

// Restore Google Consent Mode for returning visitors who already accepted.
// Runs before React mounts so analytics activates immediately on page load
// rather than waiting for the banner-driven update path. The current key
// is `vauren_cookie_consent`; a legacy hyphen key from earlier deploys is
// honoured as a fallback. CookieConsent.tsx will migrate it on mount.
const existingConsent = typeof localStorage !== "undefined"
  ? localStorage.getItem("vauren_cookie_consent") ??
    localStorage.getItem("vauren-cookie-consent")
  : null;
if (existingConsent === "accepted" && typeof window.gtag === "function") {
  window.gtag("consent", "update", {
    analytics_storage: "granted",
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
  });
}

initPostHog();
initCapacitor();
initPixels();

const ROUTE_RECOVERY_KEY = "__vauren_route_recovery__";

const isLazyImportFailure = (reason: unknown) => {
  const message =
    typeof reason === "string"
      ? reason
      : reason instanceof Error
        ? reason.message
        : typeof reason === "object" && reason && "message" in reason
          ? String((reason as { message?: unknown }).message)
          : "";

  return message.includes("Failed to fetch dynamically imported module");
};

const recoverPreviewRoute = () => {
  if (sessionStorage.getItem(ROUTE_RECOVERY_KEY) === "1") return;
  sessionStorage.setItem(ROUTE_RECOVERY_KEY, "1");
  window.location.reload();
};

if (sessionStorage.getItem(ROUTE_RECOVERY_KEY) === "1") {
  sessionStorage.removeItem(ROUTE_RECOVERY_KEY);
}

window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  recoverPreviewRoute();
});

window.addEventListener("unhandledrejection", (event) => {
  if (!isLazyImportFailure(event.reason)) return;
  event.preventDefault();
  recoverPreviewRoute();
});

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const hostname = window.location.hostname;
const isPreviewHost = hostname.includes("lovableproject.com") || hostname.includes("id-preview--") || hostname.includes("vercel.app");
const shouldEnableSentry = import.meta.env.PROD && !isPreviewHost && Boolean(sentryDsn);

const resetPreviewRuntimeCaches = async () => {
  if ("serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }

  if ("caches" in window) {
    const cacheKeys = await caches.keys();
    await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
  }
};

if (isPreviewHost) {
  resetPreviewRuntimeCaches().catch(() => {});
}

if (shouldEnableSentry) {
  Sentry.init({
    dsn: sentryDsn,
    enabled: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE,
    // Strip PII before events leave the browser. The setUser call in
    // App.tsx intentionally only sends an opaque id, but this is a
    // belt-and-suspenders pass for any integration that auto-attaches
    // user data (e.g. replay).
    beforeSend(event) {
      if (event.user) {
        delete event.user.email;
        delete event.user.username;
        delete event.user.ip_address;
      }
      return event;
    },
  });
}

createRoot(document.getElementById("root")!).render(
  // Use the existing posthog instance from lib/posthog (which is consent-gated),
  // not the apiKey form which auto-initialises and bypasses the gate.
  <PostHogProvider client={posthog}>
    <App />
  </PostHogProvider>
);

// Global Calendly event tracking
window.addEventListener("message", (e: MessageEvent) => {
  if (e.origin !== "https://calendly.com") return;
  if (e.data?.event === "calendly.event_scheduled") {
    import("./lib/conversion-pixels").then((m) => m.trackScheduleCall());
  }
});

// Register service worker for PWA support
if ('serviceWorker' in navigator && import.meta.env.PROD && !isPreviewHost) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).catch(() => {});
  });
}
